import { ReactNode, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styles from "./style.module.scss";
import PrintPage, { EXPORT_PDF_QUERY_PARAM } from "@components/print/PrintElements/PrintPage";
import PrintPageHeader from "@components/print/PrintElements/PrintPageHeader";
import PrintPageFooter from "@components/print/PrintElements/PrintPageFooter";
import { useAppSelector } from "@/store/hooks";
import PrintDocumentHeader from "@components/print/PrintElements/PrintDocumentHeader";
import DocumentInfo from "@components/print/PrintElements/DocumentInfo";
import ProductList from "@components/print/PrintElements/ProductList";
import { DebitnoteApi, DebitnoteDataType } from "@/api/DebitnoteApi";
import { DebitnoteFormDataKeys } from "@/generated/DebitnoteFormDataKeys";
import { DebitnoteRelatedAttributes } from "@/formTypes/DebitnoteDataType";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { readDocumentOtherChargesTotal } from "@utils/DocumentOtherCharges";
import { readDocumentOtherChargesLabel } from "@utils/ClientShipping";
import { formatDate } from "@utils/Api";
import { CustomerInfoBase } from "@/formTypes/DocumentClientType";
import { chunkArray, stripHtmlTags } from "@utils/Utils";
import { sortProductsByOrder } from "@utils/EDocument";
import { checkDebitnoteTicketPrint } from "@utils/ticketPrintUtils";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";

const DebitnotePrint = () => {
  const params = useParams()
  const { id: debitnoteId, autoPrint = false, copies = 0 } = params;

  const suscription = useAppSelector((state) => { return state.suscription.suscription });
  const maxItemPerPage = Number(suscription?.items_per_page || 30)
  const [debitnote, setDebitnote] = useState<DebitnoteDataType>({})
  const [debitnoteProdCount, setDebitnoteProdCount] = useState<number>(0)
  const [debitnotesProducts, setDebitnotesProducts] = useState<EDocumentProductDataType[]>()
  const [documentPages, setDocumentPages] = useState<EDocumentProductDataType[][]>()
  const [searchParams] = useSearchParams();
  const exportFlag = searchParams.get(EXPORT_PDF_QUERY_PARAM);

  useEffect(() => {
    import("@printerVariation/generic.scss");
    PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
    loadDebitnoteData()
  }, []);

  useEffect(() => {
    if (!exportFlag && autoPrint && suscription?.id && debitnote?.id) {
      checkDebitnoteTicketPrint(suscription, debitnote)
    }
  }, [suscription?.id, debitnote?.id]);

  useEffect(() => {
    const pages = chunkArray(debitnotesProducts || [], maxItemPerPage)
    setDocumentPages(pages)
  }, [maxItemPerPage, debitnotesProducts]);

  const loadDebitnoteData = async () => {
    if (debitnoteId) {
      const debitnote = await DebitnoteApi.get(debitnoteId, {
        selectionSet: [
          ...DebitnoteFormDataKeys.filter(k => !DebitnoteRelatedAttributes.includes(k)),
          "currency.*",
          "client.*",
          "invoice.*",
          "debitnoteProducts.*",
          "debitnoteProducts.product.*"
        ]
      })
      setDebitnotesProducts(sortProductsByOrder(debitnote.debitnoteProducts))
      setDebitnoteProdCount(debitnote.debitnoteProducts?.length || 0)
      setDebitnote(debitnote)
    }
    PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
  }

  function toCustomerInfoBase(creditNote: DebitnoteDataType): CustomerInfoBase {
    const { invoice } = creditNote
    if (!invoice.nombre_factura) throw new Error("nombre_factura requerido");
    return {
      nombre_factura: invoice.nombre_factura,
      razon_social: invoice.razon_social ?? "",
      tel1: invoice.tel1 ?? "",
      tel2: invoice.tel2 ?? "",
      email: invoice.email ?? "",
      direccion: invoice.direccion ?? "",
      clientActivitycode: invoice.clientActivitycode ?? "",
      plazo: invoice.plazo ?? null,
      client: invoice.client ?? null,
      tipo_identificacion: invoice.tipo_identificacion ?? "",
      identificacion: invoice.identificacion ?? "",
    };
  }

  const renderPage = (documentPages: EDocumentProductDataType[][]): ReactNode[] => {
    return documentPages.map((page, index) => {
      return (
        <PrintPage
          autoPrint={autoPrint === "1"}
          copies={Number(copies)}
        >
          <PrintPageHeader suscription={suscription} />
          <div className="page-content">
            <PrintDocumentHeader title={`Nota de debito #${debitnote.numero}`} fecha={formatDate(debitnote.created, "YYYY-MM-DD", "DD.MM.YY")} />
            <DocumentInfo
              customer={toCustomerInfoBase(debitnote)}
              docType="Factura"
              referenceData={{
                title: "Sobre Factura",
                value: ` # ${debitnote.invoice.numero}`
              }}
              docSequenceData={{
                docKey: debitnote.document_key || "",
                sequence: debitnote.sequence || ""
              }}
            />
            <ProductList
              maxItemPerPage={maxItemPerPage}
              products={page}
              productCount={debitnoteProdCount}
            />
            <PrintPageFooter
              currencyCode={debitnote.currency.currency_id}
              exchangeRate={debitnote.exchange_rate || 0}
              summary={{
                subtotal: debitnote.subtotal || 0,
                descuento: debitnote.descuento || 0,
                impuestos: debitnote.impuestos || 0,
                total: debitnote.total || 0,
                otrosCargos: readDocumentOtherChargesTotal(debitnote),
                otrosCargosLabel: readDocumentOtherChargesLabel(debitnote)
              }}
              comments={[`Por concepto de: ${stripHtmlTags(`${debitnote.description}`)}`]}
              currentPage={(index + 1)}
              totalPages={documentPages.length} />
          </div>

        </PrintPage>
      )
    });
  }

  return (
    <div>
      {debitnote?.id && documentPages && renderPage(documentPages)}
      {!debitnote.id && (
        <h3>Cargando </h3>
      )}

    </div>
  );
}

export default DebitnotePrint;
